import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import { Button, ConfigProvider, DatePicker, Form, Input, Modal, Row, Select, Space, Table, Tag } from 'antd';
import enUS from 'antd/es/locale/en_US';
import Title from 'antd/es/typography/Title';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import React, { useState } from 'react';
import { DATE_FORMAT } from '../../../../constants';
import { BlobFeatureFlag } from '../../../../generated';

const { TextArea } = Input;

const EditableCell: React.FC<any> = ({ title, editable, children, ...restProps }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(children);

  const handleSave = () => {
    setEditing(false);
    restProps.onChange(value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const renderCell = () => {
    if (editable) {
      return (
        <td {...restProps} style={{ padding: 8 }}>
          {editing ? <Input value={value} onChange={handleChange} onBlur={handleSave} autoFocus /> : <span onDoubleClick={() => setEditing(true)}>{children}</span>}
        </td>
      );
    }
    return <td {...restProps}>{children}</td>;
  };

  return renderCell();
};

interface FeatureFlagProps {
  featureFlags?: BlobFeatureFlag[];
  onSave: (data: any[]) => void;
}

export const FeatureFlags: React.FC<FeatureFlagProps> = ({ featureFlags = [], onSave }) => {
  const [selectedFeatureFlag, setSelectedFeatureFlag] = useState<any[]>(featureFlags);

  const [newIndex, setNewIndex] = useState<number | null>(null);
  const [isAddingFeatureFlag, setIsAddingFeatureFlag] = useState(false);
  const [newFeatureFlagForm] = Form.useForm();

  const handleAddNewFeatureFlag = () => {
    setIsAddingFeatureFlag(true);
  };

  const handleRemoveFlag = (index: number | null) => {
    const updatedFeatureFlag = selectedFeatureFlag.filter((_, i) => i !== index);
    setSelectedFeatureFlag(updatedFeatureFlag);
    if (index === newIndex) {
      setNewIndex(null);
    }
  };

  const handleFieldChange = (value: dayjs.Dayjs | null, index: number | null, field: string) => {
    const updatedFeatureFlag = selectedFeatureFlag.map(
      (item, i) => (i === index ? { ...item, [field]: value ? value.startOf('day').toISOString() : null } : item) // Convert dayjs to ISO string without time
    );
    setSelectedFeatureFlag(updatedFeatureFlag);
  };

  const handleValueChange = (value: string, index: number | null) => {
    const updatedFeatureFlag = selectedFeatureFlag.map((item, i) => (i === index ? { ...item, value } : item));
    setSelectedFeatureFlag(updatedFeatureFlag);
  };

  const encodeFeatureFlags = (flags: any[]) => {
    return flags.map((flag) => ({
      ...flag,
      value: DOMPurify.sanitize(flag.value)
    }));
  };

  const handleSave = (values?: BlobFeatureFlag) => {
    console.log('values', values);
    if (isAddingFeatureFlag) {
      const encodedNewFeatureFlag = {
        ...values,
        retirementDate: values?.retirementDate ? dayjs(values?.retirementDate).toISOString() : null,
        allowedValues: values?.allowedValues
          // .split(',')
          .map((value) => value.trim())
          .filter((value) => value !== '')
      };
      setSelectedFeatureFlag((prevFlags) => [...prevFlags, encodedNewFeatureFlag]);
      setIsAddingFeatureFlag(false);
      if (onSave) {
        onSave(encodeFeatureFlags([...selectedFeatureFlag, encodedNewFeatureFlag]));
      }
    } else if (onSave) {
      onSave(encodeFeatureFlags(selectedFeatureFlag));
    }
  };

  const columns = (
    handleFieldChange: (value: dayjs.Dayjs | null, index: number | null, field: string) => void,
    handleRemoveFlag: (index: number | null) => void,
    handleValueChange: (value: string, index: number | null) => void
  ) => [
    {
      title: 'Action',
      key: 'action',
      width: '5%',
      render: (_: any, _record: any, index: any) => <MinusCircleOutlined className="dynamic-delete-button" onClick={() => handleRemoveFlag(index)} />
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '15%',
      render: (text: string | number | boolean | React.ReactElement | null) => <p style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '200px' }}>{text}</p>
    },
    {
      title: 'Allowed Values',
      dataIndex: 'allowedValues',
      key: 'allowedValues',
      width: '10%',
      render: (values: string[]) => (
        <>
          {values.map((value, index) => (
            <Tag key={index}>{value}</Tag>
          ))}
        </>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '30%',
      render: (text: string | number | boolean | React.ReactElement | null) => <span>{text}</span>
    },
    {
      title: 'Retirement Date',
      dataIndex: 'retirementDate',
      key: 'retirementDate',
      width: '15%',
      render: (text: string | number | dayjs.Dayjs | null, _record: any, index: number | null) => (
        <DatePicker
          name={'retirementDate'}
          value={text ? dayjs(text).startOf('day') : null}
          format={(value) => value.format(DATE_FORMAT).toUpperCase()}
          onChange={(date) => handleFieldChange(date, index, 'retirementDate')}
          size="small"
          allowClear
          disabledDate={(current: any) => current && current < dayjs().endOf('day').subtract(1, 'day')}
        />
      )
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '35%',
      render: (text: string | number | boolean | React.ReactElement | null, _record: any, index: number | null) => (
        <EditableCell editable onChange={(newValue: string) => handleValueChange(newValue, index)}>
          {typeof text === 'string' ? text : ''}
        </EditableCell>
      )
    }
  ];

  const handleCancel = () => {
    setIsAddingFeatureFlag(false);
  };

  const addFeatureFlagModal = () => {
    return (
      <Modal open={isAddingFeatureFlag} title="Add New Feature Flag" footer={null} onCancel={handleCancel}>
        <Form layout="vertical" form={newFeatureFlagForm} onFinish={handleSave}>
          <Form.Item
            label="Name:"
            name={'name'}
            rules={[
              {
                required: true,
                message: 'Please enter a name'
              }
            ]}
          >
            <Input style={{ width: '100%' }} placeholder="Name" />
          </Form.Item>
          <Form.Item
            label="Allowed values:"
            name={'allowedValues'}
            rules={[
              {
                required: true,
                message: 'Please enter allowed values'
              }
            ]}
          >
            <Select suffixIcon={null} open={false} mode="tags" style={{ width: '100%' }} placeholder="Type and press Enter" />
            {/* <Input style={{ width: '100%' }} placeholder="Allowed values" /> */}
          </Form.Item>
          <Form.Item
            label="Description:"
            name={'description'}
            rules={[
              {
                required: true,
                message: 'Please enter a description'
              }
            ]}
          >
            <TextArea rows={4} placeholder="Description" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item            
            name={'retirementDate'}
            label="Retirement date:"
            rules={[
              {
                required: true,
                message: 'Please enter a retirement date'
              }
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Retirement date"
              format={(value) => value.format(DATE_FORMAT).toUpperCase()}
              allowClear
              disabledDate={(current: any) => current && current < dayjs().endOf('day').subtract(1, 'day')}
            />
          </Form.Item>
          <Form.Item
            name={'value'}
            label="Value:"
            rules={[
              {
                required: true,
                message: 'Please enter a value'
              }
            ]}
          >
            <TextArea rows={4} placeholder="Value" style={{ width: '100%' }} />
          </Form.Item>
          <Row justify={'center'}>
            <Space direction="horizontal" style={{ alignItems: 'baseline' }}>
              <Form.Item>
                <Button type="default" onClick={handleCancel}>
                  Cancel
                </Button>
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Save
                </Button>
              </Form.Item>
            </Space>
          </Row>
        </Form>
      </Modal>
    );
  };
  return (
    <>
      <PageHeader>
        <div id="leftTitle" style={{ marginBottom: '30px' }}>
          <Title level={3} style={{ display: 'inline' }}>
            Feature Flags
          </Title>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <Title level={4} type="secondary" style={{ display: 'inline' }}>
            Settings
          </Title>
        </div>
      </PageHeader>
      <ConfigProvider locale={enUS}>
        <Table
          columns={columns(handleFieldChange, handleRemoveFlag, handleValueChange)}
          dataSource={selectedFeatureFlag}
          rowKey="name"
          pagination={false}
          scroll={{ x: '1000' }}
          size="small"
          rowHoverable={false}
          style={{ width: '100%', fontSize: '14px', tableLayout: 'fixed', border: '1px solid #f0f0f0', borderRadius: '10px', padding: '15px', marginBottom: '10px' }}
        />
        <Row justify={'center'}>
          <Button type="dashed" onClick={handleAddNewFeatureFlag} block style={{ width: '100%', marginBottom: '10px', float: 'inline-start' }}>
            <PlusOutlined /> Add Feature Flag
          </Button>
        </Row>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel} style={{ marginRight: '10px' }}>
            Cancel
          </Button>
          <Button type="primary" onClick={() => handleSave()}>
            Save
          </Button>
        </div>

        {addFeatureFlagModal()}
      </ConfigProvider>
    </>
  );
};
